import React, { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  Table,
  Space,
  Empty,
  Tag,
  Modal,
  Card,
  Row,
  Col,
  Statistic,
  Pagination
} from 'antd'
import {
  ReloadOutlined,
  ArrowLeftOutlined,
  TeamOutlined,
  RedoOutlined,
  EditOutlined
} from '@ant-design/icons'
import PageLoader from '../../../components/Layouts/PageLoader'
import {
  getRequest,
  dutyBroadcastDetailUrl,
  dutyBroadcastRecipientsUrl,
  dutyBroadcastStatsUrl
} from '../../../config'
import CustomerDetailBody from '../Customers/CustomerDetailBody'
import { broadcastStatusTag, recipientStatusTag } from './statusColors'
import IdentityLabel from '../../../components/IdentityLabel'
import retryRecipients from './retryRecipients'

const fmt = v => (v ? new Date(v).toLocaleString() : '—')

// Broadcast detail + delivery/response tracking. Stats and per-recipient state arrive
// asynchronously (status callbacks), so a Reload action re-pulls both.
const BroadcastDetail = props => {
  const broadcastId = props.match.params.id
  const [loading, setLoading] = useState(false)
  const [broadcast, setBroadcast] = useState(null)
  const [stats, setStats] = useState(null)
  // The recipient list is paginated server-side; without a page param only the first page comes
  // back and the rest of the list is unreachable.
  const [recipients, setRecipients] = useState([])
  const [recMeta, setRecMeta] = useState({ count: 0, page: 1, pageSize: 50 })
  // Held in a ref, not state: load() reads the current page on refresh, and depending on state
  // here would change load()'s identity on every fetch and re-trigger the effect that calls it.
  const recPageRef = useRef(1)
  const [quickViewId, setQuickViewId] = useState(null)
  const [retrying, setRetrying] = useState(false)

  const loadRecipients = useCallback(
    async (page = 1) => {
      const r = await getRequest(
        `${dutyBroadcastRecipientsUrl(broadcastId)}?page=${page}`,
        'recipients',
        () => {}
      )
      if (!r.success) return
      const rows = r.data.results || r.data
      const count = r.data.count == null ? rows.length : r.data.count
      const pageSize = r.data.pageSize || 50
      // The list can shrink while this page is open (recipients removed on the edit screen, then
      // a refresh here), which can leave the current page past the end. Step back instead of
      // showing an empty table with no way out. Mirrors RecipientSelect.
      if (page > 1 && rows.length === 0 && count > 0) {
        return loadRecipients(Math.max(1, Math.ceil(count / pageSize)))
      }
      recPageRef.current = r.data.page || page
      setRecipients(rows)
      setRecMeta({ count, page: r.data.page || page, pageSize })
    },
    [broadcastId]
  )

  const load = useCallback(async () => {
    const [b, s] = await Promise.all([
      getRequest(dutyBroadcastDetailUrl(broadcastId), 'broadcast', setLoading),
      getRequest(dutyBroadcastStatsUrl(broadcastId), 'stats', () => {})
    ])
    if (b.success) setBroadcast(b.data)
    if (s.success) setStats(s.data)
    loadRecipients(recPageRef.current)
  }, [broadcastId, loadRecipients])

  useEffect(() => {
    load()
  }, [load])

  const byStatus = (stats && stats.byStatus) || {}
  const failedCount = byStatus.failed || 0

  // Requeue failed recipients (a single row, or all failed when recipientIds is omitted). Shared
  // with the recipients page so retrying behaves identically in both places.
  const doRetry = recipientIds =>
    retryRecipients({
      broadcastId,
      recipientIds,
      failedCount,
      setLoading: setRetrying,
      onDone: load
    })

  const columns = [
    {
      title: 'Recipient',
      key: 'recipient',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Space size={6}>
            <IdentityLabel value={r} strong />
            {r.alreadySent ? (
              <Tag color="volcano" style={{ marginBottom: 0 }}>
                sent {r.sendCount}×
              </Tag>
            ) : null}
          </Space>
          {r.displayName ? (
            <span style={{ color: '#888', fontSize: 12 }}>{r.displayName}</span>
          ) : null}
        </Space>
      )
    },
    { title: 'Status', key: 'status', render: (_, r) => recipientStatusTag(r.status) },
    { title: 'Sent', dataIndex: 'lastSentAt', key: 'lastSentAt', render: fmt },
    { title: 'Delivered', dataIndex: 'deliveredAt', key: 'deliveredAt', render: fmt },
    { title: 'Read', dataIndex: 'readAt', key: 'readAt', render: fmt },
    {
      title: 'Responded',
      key: 'responded',
      render: (_, r) =>
        r.respondedAt ? (
          <Space direction="vertical" size={0}>
            <Tag color="green">replied</Tag>
            {r.responsePreview ? (
              <span style={{ color: '#888', fontSize: 12 }}>{r.responsePreview}</span>
            ) : null}
          </Space>
        ) : (
          '—'
        )
    },
    {
      title: 'Note',
      key: 'note',
      render: (_, r) =>
        r.skipReason ? (
          <Tag color="orange">{r.skipReason}</Tag>
        ) : r.error ? (
          <Tag color="red">{r.error}</Tag>
        ) : null
    },
    {
      title: '',
      key: 'action',
      width: 150,
      render: (_, r) => (
        <Space size={4}>
          {r.status === 'failed' ? (
            <Button
              size="small"
              icon={<RedoOutlined />}
              onClick={() => doRetry([r.id])}
            >
              Retry
            </Button>
          ) : null}
          {r.customer || r.customerId ? (
            <Button
              size="small"
              onClick={() => setQuickViewId(r.customer || r.customerId)}
            >
              View
            </Button>
          ) : null}
        </Space>
      )
    }
  ]

  return (
    <PageLoader className="container" loading={loading}>
      <div className="card mb-5rem">
        <div className="card-body">
          <Space style={{ marginBottom: 8 }} wrap>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => props.history.push('/settings/broadcasts')}
            >
              Back
            </Button>
            <Button icon={<ReloadOutlined />} onClick={load}>
              Reload
            </Button>
            {failedCount > 0 ? (
              <Button
                danger
                icon={<RedoOutlined />}
                loading={retrying}
                onClick={() => doRetry(null)}
              >
                Retry failed ({failedCount})
              </Button>
            ) : null}
            {broadcast ? (
              <>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() =>
                    props.history.push(
                      `/settings/broadcasts/${broadcastId}/compose`
                    )
                  }
                >
                  Edit message
                </Button>
                <Button
                  onClick={() =>
                    props.history.push(
                      `/settings/broadcasts/${broadcastId}/recipients`
                    )
                  }
                >
                  Edit recipients
                </Button>
              </>
            ) : null}
          </Space>

          <h3 style={{ marginBottom: 4 }}>
            {broadcast ? broadcast.name : 'Broadcast'}{' '}
            {broadcast ? broadcastStatusTag(broadcast.status) : null}
          </h3>
          {broadcast && broadcast.body ? (
            <p style={{ color: '#888', whiteSpace: 'pre-wrap' }}>{broadcast.body}</p>
          ) : null}
          {broadcast && broadcast.promotionDetail ? (
            <p style={{ marginBottom: 12 }}>
              <Tag color="magenta">Promotion</Tag>
              {broadcast.promotionDetail.name} — {broadcast.promotionDetail.discountPercent}% off
              {broadcast.promotionDetail.isLive ? (
                <Tag color="green" style={{ marginLeft: 6 }}>
                  live
                </Tag>
              ) : (
                <Tag style={{ marginLeft: 6 }}>not live</Tag>
              )}
            </p>
          ) : null}

          {stats ? (
            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={6} md={3}>
                <Card size="small">
                  <Statistic
                    title="Recipients"
                    value={stats.total}
                    prefix={<TeamOutlined />}
                  />
                </Card>
              </Col>
              {['pending', 'sent', 'delivered', 'read', 'failed', 'skipped'].map(k => (
                <Col xs={12} sm={6} md={3} key={k}>
                  <Card size="small">
                    <Statistic title={k} value={byStatus[k] || 0} />
                  </Card>
                </Col>
              ))}
              <Col xs={12} sm={6} md={3}>
                <Card size="small">
                  <Statistic
                    title="Responded"
                    value={stats.responded}
                    suffix={
                      stats.sentTotal
                        ? `(${Math.round((stats.responseRate || 0) * 100)}%)`
                        : ''
                    }
                  />
                </Card>
              </Col>
            </Row>
          ) : null}

          <Table
            rowKey="id"
            size="small"
            dataSource={recipients}
            columns={columns}
            scroll={{ x: 1000 }}
            pagination={false}
            locale={{ emptyText: <Empty description="No recipients" /> }}
          />
          {recMeta.count > recMeta.pageSize ? (
            <div style={{ textAlign: 'right', marginTop: 12 }}>
              <Pagination
                current={recMeta.page}
                total={recMeta.count}
                pageSize={recMeta.pageSize}
                showSizeChanger={false}
                showTotal={t => `${t} recipients`}
                onChange={page => loadRecipients(page)}
              />
            </div>
          ) : null}
        </div>
      </div>

      <Modal
        open={!!quickViewId}
        onCancel={() => setQuickViewId(null)}
        footer={null}
        width={880}
        title="Customer detail"
        destroyOnClose
      >
        {quickViewId ? <CustomerDetailBody id={quickViewId} /> : null}
      </Modal>
    </PageLoader>
  )
}

BroadcastDetail.propTypes = {
  history: PropTypes.object,
  match: PropTypes.object
}

export default BroadcastDetail
